import React, { useRef, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import { useAuth } from '../../context/AuthContext';
import { FileText, Globe, Rocket, Mail, Users, Lightbulb, Target, ArrowRight } from 'lucide-react';

const SUGGESTIONS = [
  {
    icon: FileText,
    title: 'Draft a client proposal',
    prompt: 'Help me write a professional proposal for an AI automation project. Include sections for scope, deliverables, timeline, and pricing structure.',
  },
  {
    icon: Globe,
    title: 'Landing page copy',
    prompt: 'Write compelling copy for a landing page that sells AI chatbot and automation services to small and mid-size businesses.',
  },
  {
    icon: Rocket,
    title: 'Client acquisition strategy',
    prompt: 'What are the most effective strategies to find and close new clients for an AI automation agency? Give me a concrete 30-day action plan.',
  },
  {
    icon: Mail,
    title: 'Cold outreach sequence',
    prompt: 'Write a 3-email cold outreach sequence for reaching business owners who could benefit from AI automation. Make it personal and non-salesy.',
  },
  {
    icon: Users,
    title: 'Client onboarding flow',
    prompt: 'Design a streamlined client onboarding workflow for my AI agency. Include key milestones, intake questions, and a kickoff meeting agenda.',
  },
  {
    icon: Lightbulb,
    title: 'LinkedIn content plan',
    prompt: 'Create a week of LinkedIn post ideas that position me as a thought leader in AI business automation. Include hooks and key talking points for each.',
  },
  {
    icon: Target,
    title: 'Where do I find leads?',
    prompt: 'Where can I find leads and do prospecting inside Able?',
  },
];

function SuggestionCard({ icon: Icon, title, prompt, onClick, index }) {
  return (
    <button
      onClick={() => onClick(prompt)}
      className="group text-left w-full rounded-xl border border-gray-150 dark:border-gray-700/80 bg-white dark:bg-gray-800/50 p-4 transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm hover:-translate-y-0.5"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[0.8125rem] font-semibold text-gray-800 dark:text-gray-200">{title}</span>
            <ArrowRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-400 transition-all group-hover:translate-x-0.5 flex-shrink-0" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed line-clamp-2">{prompt}</p>
        </div>
      </div>
    </button>
  );
}

function EmptyState({ onSuggestionClick }) {
  const { user } = useAuth();
  const firstName = user?.full_name?.split(' ')[0];

  return (
    <div className="h-full flex items-start justify-center px-4 pt-16">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            {firstName ? `What can I help you with, ${firstName}?` : 'What can I help you with?'}
          </h2>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Pick a suggestion or start typing your own message</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SUGGESTIONS.map((suggestion, i) => (
            <SuggestionCard key={i} {...suggestion} index={i} onClick={onSuggestionClick} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SkeletonLine({ width }) {
  return <div className={`h-3.5 rounded-md bg-gray-200 dark:bg-gray-700/60 ${width}`} />;
}

function MessageSkeleton({ type }) {
  if (type === 'user') {
    return (
      <div className="flex justify-end mb-6 animate-pulse">
        <div className="max-w-[75%] space-y-2.5 bg-brand-50/60 dark:bg-brand-950/20 border border-brand-200/40 dark:border-brand-800/30 rounded-2xl rounded-tr-sm px-4 py-3">
          <SkeletonLine width="w-56" />
          <SkeletonLine width="w-36" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start mb-6 animate-pulse">
      <div className="flex items-start gap-3 w-[85%]">
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700/60 mt-1" />
        <div className="flex-1 space-y-2.5 pt-1.5">
          <SkeletonLine width="w-full" />
          <SkeletonLine width="w-5/6" />
          <SkeletonLine width="w-4/6" />
          <SkeletonLine width="w-3/4" />
        </div>
      </div>
    </div>
  );
}

function ChatSkeleton() {
  return (
    <div className="h-full overflow-hidden px-4 py-4">
      <div className="max-w-3xl mx-auto">
        <MessageSkeleton type="user" />
        <MessageSkeleton type="assistant" />
        <MessageSkeleton type="user" />
        <MessageSkeleton type="assistant" />
      </div>
    </div>
  );
}

export default function MessageList({ messages, isLoadingChat, onSuggestionClick, onSendMessage }) {
  const containerRef = useRef(null);
  const shouldAutoScroll = useRef(true);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScroll.current = distanceFromBottom < 100;
  };

  useEffect(() => {
    const el = containerRef.current;
    if (el && shouldAutoScroll.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  if (isLoadingChat) {
    return <ChatSkeleton />;
  }

  if (messages.length === 0) {
    return <EmptyState onSuggestionClick={onSuggestionClick} />;
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="h-full overflow-y-auto px-4 py-4"
    >
      <div className="max-w-3xl mx-auto pb-48">
        {messages.map(msg => (
          <MessageBubble key={msg.id} message={msg} onSendMessage={onSendMessage} />
        ))}
      </div>
    </div>
  );
}
